import { Outlet, Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useState, useEffect } from "react";
import { useColorScheme } from "../hooks/useColorScheme";
import { LoadingOverlay } from "./LoadingOverlay";
import { motion, AnimatePresence } from "framer-motion";

const menuVariants = {
    open: {
        opacity: 1,
        height: "auto",
        transition: {
            duration: 0.3,
            ease: "easeOut"
        }
    },
    closed: {
        opacity: 0,
        height: 0,
        transition: {
            duration: 0.2,
            ease: "easeIn"
        }
    }
};

function NavLink({ 
    to, 
    children, 
    className = "", 
    onClick,
    icon
}: { 
    to: string; 
    children: React.ReactNode; 
    className?: string;
    onClick?: () => void;
    icon?: React.ReactNode;
}) {
    const location = useLocation();
    const isActive = location.pathname === to;
    
    return (
        <Link
            to={to}
            onClick={onClick}
            className={`nav-link ${isActive ? 'nav-link-active' : ''} ${className}`}
        >
            {icon && <span className="mr-2">{icon}</span>}
            {children}
        </Link>
    );
}

export default function Layout() {
    const { user, signOut, loading } = useAuth();
    const { isDark, isTransitioning, toggleColorScheme } = useColorScheme({
        storageKey: 'darkMode',
        transitionDuration: 200
    });
    const [isMobileMenuOpen, setMobileMenuOpen] = useState(false);
    const navigate = useNavigate();
    const [scrolled, setScrolled] = useState(false);

    // Handle scroll effect for header
    useEffect(() => {
        const handleScroll = () => {
            setScrolled(window.scrollY > 10);
        };
        
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleSignOut = async () => {
        try {
            setMobileMenuOpen(false);
            await signOut();
            navigate("/");
        } catch (error) {
            console.error("Error signing out:", error);
        }
    };

    return (
        <div className="min-h-screen flex flex-col bg-background text-text mobile-py-safe">
            <header className={`sticky top-0 z-50 backdrop-blur-xl mobile-px-safe transition-all duration-200 ${
                scrolled 
                    ? 'bg-white/90 dark:bg-background/90 shadow-sm' 
                    : 'bg-background dark:bg-background border-b border-gray-200/70 dark:border-gray-800/70'
            }`}>
                <div className="container-md">
                    <nav className="flex h-[var(--header-height)] items-center justify-between gap-4">
                        <div className="flex items-center space-x-6">
                            {user && (
                                <button
                                    onClick={() => setMobileMenuOpen(!isMobileMenuOpen)}
                                    className="sm:hidden h-[var(--mobile-touch-target-size)] w-[var(--mobile-touch-target-size)] -ml-3 flex items-center justify-center rounded-full text-text-secondary hover:text-text hover:bg-gray-100/80 dark:hover:bg-gray-800/80 transition-colors active:bg-gray-200 dark:active:bg-gray-700"
                                    aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
                                >
                                    {isMobileMenuOpen ? (
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    ) : (
                                        <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6h16M4 12h16M4 18h16" />
                                        </svg>
                                    )}
                                </button>
                            )}
                            <Link 
                                to="/" 
                                className="flex items-center py-2 select-none tap-highlight-none touch-manipulation"
                                aria-label="Go to home page"
                            >
                                <span className="text-lg font-bold text-primary">
                                    Whisper<span className="text-text dark:text-text">Web</span>
                                </span>
                            </Link>

                            {/* Desktop Navigation */}
                            {user && (
                                <div className="hidden sm:flex items-center gap-3">
                                    <NavLink 
                                        to="/dashboard"
                                        icon={
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                            </svg>
                                        }
                                    >
                                        Dashboard
                                    </NavLink>
                                    <NavLink 
                                        to="/history"
                                        icon={
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                            </svg>
                                        }
                                    >
                                        Transcriptions
                                    </NavLink>
                                    <NavLink 
                                        to="/profile"
                                        icon={
                                            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                            </svg>
                                        }
                                    >
                                        Profile
                                    </NavLink>
                                </div>
                            )}
                        </div>

                        <div className="flex items-center space-x-3">
                            {user ? (
                                <button
                                    onClick={handleSignOut}
                                    className="btn-slim btn-ghost"
                                >
                                    <span className="hidden sm:inline">Sign Out</span>
                                    <svg className="sm:hidden w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                                    </svg>
                                </button>
                            ) : (
                                <NavLink
                                    to="/auth"
                                    className="btn btn-primary"
                                >
                                    Sign In
                                </NavLink>
                            )}
                            <button
                                onClick={toggleColorScheme}
                                disabled={isTransitioning}
                                className="h-[var(--mobile-touch-target-size)] w-[var(--mobile-touch-target-size)] flex items-center justify-center rounded-full text-text-secondary hover:text-text hover:bg-gray-100/80 dark:hover:bg-gray-800/80 disabled:opacity-50 disabled:cursor-not-allowed transition-colors active:bg-gray-200 dark:active:bg-gray-700"
                                aria-label={isDark ? "Switch to light mode" : "Switch to dark mode"}
                            >
                                {isDark ? (
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                    </svg>
                                ) : (
                                    <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                    </svg>
                                )}
                            </button>
                        </div>
                    </nav>
                </div>
            </header>

            {/* Enhanced Mobile menu with smooth animations */}
            <AnimatePresence>
                {user && isMobileMenuOpen && (
                    <motion.div
                        variants={menuVariants}
                        initial="closed"
                        animate="open"
                        exit="closed"
                        className="sm:hidden fixed top-[var(--header-height)] left-0 right-0 z-40 overflow-hidden bg-white dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700 shadow-md mobile-px-safe backdrop-blur-lg bg-opacity-95 dark:bg-opacity-95"
                    >
                        <div className="container-lg py-4 space-y-2">
                            <NavLink
                                to="/dashboard"
                                className="flex w-full items-center"
                                onClick={() => setMobileMenuOpen(false)}
                                icon={
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2V6zM14 6a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2V6zM4 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2H6a2 2 0 01-2-2v-2zM14 16a2 2 0 012-2h2a2 2 0 012 2v2a2 2 0 01-2 2h-2a2 2 0 01-2-2v-2z" />
                                    </svg>
                                }
                            >
                                Dashboard
                            </NavLink>
                            <NavLink
                                to="/history"
                                className="flex w-full items-center"
                                onClick={() => setMobileMenuOpen(false)}
                                icon={
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                                    </svg>
                                }
                            >
                                Transcriptions
                            </NavLink>
                            <NavLink
                                to="/profile"
                                className="flex w-full items-center"
                                onClick={() => setMobileMenuOpen(false)}
                                icon={
                                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                                    </svg>
                                }
                            >
                                Profile
                            </NavLink>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Main content with mobile optimizations */}
            <main className="page-container mobile-px-safe scroll-container">
                {loading ? (
                    <div className="flex-1 flex items-center justify-center">
                        <LoadingOverlay message="Loading..." />
                    </div>
                ) : (
                    <div className="page-content">
                        <div className="content-container">
                            <Outlet />
                        </div>
                    </div>
                )}
            </main>

            {/* Modern, minimal footer */}
            <footer className="py-8 border-t border-gray-200/70 dark:border-gray-800/70 bg-background mobile-px-safe">
                <div className="container-lg">
                    <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-6 md:space-y-0">
                        <div className="flex flex-col">
                            <div className="flex items-center">
                                <span className="text-lg font-bold text-primary">Whisper<span className="text-text dark:text-text">Web</span></span>
                            </div>
                            <p className="mt-1.5 text-sm text-text-secondary">
                                Fast, accurate speech-to-text in your browser
                            </p>
                        </div>
                        
                        <div className="flex flex-wrap gap-8 text-sm">
                            <a 
                                href="https://github.com/openai/whisper" 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="text-text-secondary hover:text-primary transition-colors py-2"
                            >
                                GitHub
                            </a>
                            <a 
                                href="#" 
                                className="text-text-secondary hover:text-primary transition-colors py-2"
                            >
                                Privacy
                            </a>
                            <a 
                                href="#" 
                                className="text-text-secondary hover:text-primary transition-colors py-2"
                            >
                                Terms
                            </a>
                        </div>
                    </div>
                    
                    <div className="mt-8 pt-6 border-t border-gray-200/70 dark:border-gray-800/70">
                        <p className="text-xs text-text-secondary">
                            © {new Date().getFullYear()} WhisperWeb. All rights reserved.
                        </p>
                    </div>
                </div>
            </footer>
        </div>
    );
}
